import { EditorState, ContentState, Modifier } from "draft-js";
import htmlToDraft from "html-to-draftjs";

const BLOCK_TAGS = new Set([
  "p", "div", "h1", "h2", "h3", "h4", "h5", "h6",
  "blockquote", "section", "article", "header", "footer",
  "tr", "table", "thead", "tbody", "pre",
]);
const LIST_TAGS = new Set(["ul", "ol"]);
const BOLD_TAGS = new Set(["strong", "b", "h1", "h2", "h3", "h4", "h5", "h6"]);
const ITALIC_TAGS = new Set(["em", "i"]);
const PARAGRAPH_TAGS = new Set(["p", "h1", "h2", "h3", "h4", "h5", "h6"]);

const escapeHtml = (text) =>
  String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const isBoldStyle = (style) =>
  /font-weight\s*:\s*(bold|[6-9]00)/i.test(style || "");

const isExplicitlyNotBold = (style) =>
  /font-weight\s*:\s*(normal|[1-5]00)\b/i.test(style || "");

const isItalicStyle = (style) =>
  /font-style\s*:\s*italic/i.test(style || "");

const isExplicitlyNotItalic = (style) =>
  /font-style\s*:\s*normal/i.test(style || "");

const isUnderlineStyle = (style) =>
  /text-decoration(?:-line)?\s*:[^;]*underline/i.test(style || "");

const wrapEachParagraph = (html, wrapFn) =>
  String(html || "")
    .split(/<\/p><p>/i)
    .map(wrapFn)
    .join("</p><p>");

const unwrapEmptyParagraphs = (html) => {
  let next = String(html || "").replace(/<p>(?:\s|&nbsp;|\u00a0)*<\/p>/gi, "");
  if (!next.trim()) return "";
  if (!/^\s*<(p|ul|ol)\b/i.test(next)) next = `<p>${next}`;
  if (!/<\/(p|ul|ol)>\s*$/i.test(next)) next = `${next}</p>`;
  return next
    .replace(/<p>(?:\s|&nbsp;|\u00a0)*<\/p>/gi, "")
    .replace(/<p>\s+/g, "<p>")
    .replace(/\s+<\/p>/g, "</p>")
    .replace(/<p>(?:<(?:strong|em|u)>\s*<\/(?:strong|em|u)>\s*)+<\/p>/gi, "")
    .replace(/(<\/p>)\s*(<p>)/gi, "$1$2")
    .replace(/<p>\s*(<(?:ul|ol)\b)/gi, "$1")
    .replace(/(<\/(?:ul|ol)>)\s*<\/p>/gi, "$1");
};

const extractFragment = (html) => {
  const match = String(html).match(
    /<!--StartFragment-->([\s\S]*?)<!--EndFragment-->/i
  );
  return match ? match[1] : html;
};

const serializeTextNode = (node) => {
  const collapsed = (node.textContent || "")
    .replace(/[\r\n\t]+/g, " ")
    .replace(/ {2,}/g, " ");
  if (!collapsed.trim()) return collapsed.includes(" ") ? " " : "";
  return escapeHtml(collapsed);
};

const serializeNode = (node, ctx = { inList: false, inParagraph: false }) => {
  if (!node) return "";
  if (node.nodeType === Node.COMMENT_NODE) return "";
  if (node.nodeType === Node.TEXT_NODE) return serializeTextNode(node);
  if (node.nodeType !== Node.ELEMENT_NODE) return "";

  const tag = node.tagName.toLowerCase();
  if (tag === "style" || tag === "script" || tag === "meta" || tag === "link" || tag === "title" || tag === "xml") {
    return "";
  }
  // Word wraps long paragraphs in clipboard HTML with a <br>. That is not an
  // intentional line break — keep those headings/sentences on one line.
  if (tag === "br") {
    if (ctx.inList) return "<br>";
    if (ctx.inParagraph) return " ";
    return "</p><p>";
  }
  if (tag === "img") return "";

  const style = node.getAttribute("style") || "";
  const nextCtx = {
    inList: ctx.inList || LIST_TAGS.has(tag) || tag === "li",
    inParagraph: ctx.inParagraph || PARAGRAPH_TAGS.has(tag),
  };
  const inner = Array.from(node.childNodes)
    .map((child) => serializeNode(child, nextCtx))
    .join("");
  if (!inner && tag !== "li") return "";

  const makeBold = (BOLD_TAGS.has(tag) && !isExplicitlyNotBold(style)) || isBoldStyle(style);
  const makeItalic = (ITALIC_TAGS.has(tag) && !isExplicitlyNotItalic(style)) || isItalicStyle(style);
  const makeUnderline = tag === "u" || isUnderlineStyle(style);
  const html = wrapEachParagraph(inner, (part) => {
    let next = part;
    if (makeBold) next = `<strong>${next}</strong>`;
    if (makeItalic) next = `<em>${next}</em>`;
    if (makeUnderline) next = `<u>${next}</u>`;
    return next;
  });

  if (LIST_TAGS.has(tag)) return `<${tag}>${html}</${tag}>`;
  if (tag === "li") return `<li>${html}</li>`;

  const hasBlockChild = Array.from(node.children).some((child) => {
    const childTag = child.tagName.toLowerCase();
    return BLOCK_TAGS.has(childTag) || LIST_TAGS.has(childTag);
  });
  if (BLOCK_TAGS.has(tag) && !hasBlockChild) return `<p>${html}</p>`;
  return html;
};

export const normalizePastedHtml = (html) => {
  if (!html || typeof window === "undefined" || typeof DOMParser === "undefined") {
    return "";
  }
  const fragment = extractFragment(html);
  const doc = new DOMParser().parseFromString(fragment, "text/html");
  return unwrapEmptyParagraphs(serializeNode(doc.body));
};

const applySimpleMarkdown = (escapedText) =>
  escapedText
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/__(.+?)__/g, "<strong>$1</strong>");

export const plainTextToArticleHtml = (text) => {
  const normalized = String(text || "")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .replace(/\u00a0/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .replace(/^\n+|\n+$/g, "");
  if (!normalized.trim()) return "";

  return normalized
    .split(/\n\s*\n/)
    .map((para) => {
      const withBreaks = escapeHtml(para).replace(/\n/g, " ");
      return `<p>${applySimpleMarkdown(withBreaks)}</p>`;
    })
    .join("");
};

const htmlTextLength = (html) =>
  String(html || "")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .trim().length;

export const pastedArticleToHtml = (text, html) => {
  const fromHtml = html ? normalizePastedHtml(html) : "";
  const plainHtml = plainTextToArticleHtml(text);
  if (fromHtml && htmlTextLength(fromHtml) >= Math.max(1, htmlTextLength(plainHtml) * 0.5)) {
    return fromHtml;
  }
  return plainHtml;
};

const trimEmptyBlocks = (blocks) => {
  let next = blocks.slice();
  const isEmpty = (block) => !block.getText().replace(/\u00a0/g, " ").trim();
  while (next.length && isEmpty(next[0])) next = next.slice(1);
  while (next.length && isEmpty(next[next.length - 1])) {
    next = next.slice(0, -1);
  }
  return next;
};

export const insertPastedArticleHtml = (editorState, html) => {
  const contentBlock = htmlToDraft(html || "");
  if (!contentBlock || !contentBlock.contentBlocks || !contentBlock.contentBlocks.length) {
    return null;
  }
  const blocks = trimEmptyBlocks(contentBlock.contentBlocks);
  if (!blocks.length) return null;

  const current = editorState && typeof editorState.getCurrentContent === "function"
    ? editorState
    : EditorState.createEmpty();
  const currentContent = current.getCurrentContent();
  const selection = current.getSelection();
  const pastedContent = ContentState.createFromBlockArray(blocks, contentBlock.entityMap);
  const newContent = Modifier.replaceWithFragment(
    currentContent,
    selection,
    pastedContent.getBlockMap()
  );
  return EditorState.push(current, newContent, "insert-fragment");
};
